import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function ShopPage({ user, embedded = false, initialPortal = null }) {
  const [portal, setPortal] = useState(initialPortal);
  const [loading, setLoading] = useState(!initialPortal);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("items");
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);
  const token = localStorage.getItem("kami_token");

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    if (initialPortal?.shop) {
      setPortal(initialPortal);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/me/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPortal(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load shop.");
    } finally {
      setLoading(false);
    }
  }, [token, initialPortal]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user || !token) {
    return (
      <div className={embedded ? "portal-embed" : "portal-page"}>
        <div className="profile-login-gate">
          {!embedded && <h1>Shop</h1>}
          <p>Log in with Discord to browse and buy with Essence.</p>
          <button
            type="button"
            className="nav-login-btn"
            onClick={() => {
              window.location.href = `${API_BASE}/auth/login`;
            }}
          >
            Log in with Discord
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={embedded ? "portal-embed" : "portal-page"}>
        <div className="page-loading">
          <div className="sync-spinner" />
        </div>
      </div>
    );
  }

  if (error || !portal) {
    return (
      <div className={embedded ? "portal-embed" : "portal-page"}>
        <div className="profile-login-gate">
          {!embedded && <h1>Shop</h1>}
          <p>{error || "Unavailable."}</p>
          {!embedded && <Link to="/profile">Back to profile</Link>}
        </div>
      </div>
    );
  }

  const shop = portal.shop || {};
  const lists = {
    items: shop.items || [],
    luxury: shop.luxury || [],
    titles: shop.titles || [],
    themes: shop.themes || [],
  };

  const buy = async (entry) => {
    setBusy(entry.key);
    setFlash(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/me/shop/buy`,
        { key: entry.key, catalog: entry.catalog || tab },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.portal) setPortal({ ...portal, ...data.portal });
      const spent = Number(data.price || entry.price).toLocaleString();
      if (tab === "titles" || tab === "themes") {
        setFlash(
          `Unlocked ${entry.name} (−${spent} Essence). Equip it from your profile.`,
        );
      } else {
        setFlash(`Bought ${entry.name} (−${spent} Essence)`);
      }
    } catch (err) {
      setFlash(err.response?.data?.error || "Purchase failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={embedded ? "portal-embed" : "portal-page"}>
      <header className="portal-page-head">
        {!embedded && <h1>Shop</h1>}
        <p>
          Essence: <strong>{(portal.essence || 0).toLocaleString()}</strong>
        </p>
      </header>
      {flash && <p className="portal-flash">{flash}</p>}
      <div className="portal-tabs">
        {["items", "luxury", "titles", "themes"].map((t) => (
          <button
            key={t}
            type="button"
            className={`portal-tab${tab === t ? " active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="shop-grid">
        {lists[tab].map((entry) => (
          <div key={`${tab}-${entry.key}`} className="shop-card">
            <div className="shop-card-top">
              <span className="emoji">{entry.emoji || "✦"}</span>
              <strong>{entry.name}</strong>
            </div>
            <p className="portal-muted">{entry.description}</p>
            <div className="shop-card-foot">
              <span>{Number(entry.price || 0).toLocaleString()} Essence</span>
              <button
                type="button"
                className="profile-btn"
                disabled={busy || entry.owned}
                onClick={() => buy({ ...entry, catalog: tab })}
              >
                {entry.owned ? "Owned" : busy === entry.key ? "…" : "Buy"}
              </button>
            </div>
          </div>
        ))}
      </div>
      {(portal.inventory || []).length > 0 && (
        <section className="portal-section shop-inventory">
          <h2>Inventory</h2>
          <p className="portal-muted">{portal.inventory.join(" · ")}</p>
        </section>
      )}
    </div>
  );
}
